import { Radio, Stack } from '@mui/joy';
import { t } from 'i18next';
import { SyntheticEvent } from 'react';
import { Control, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IRadioChoice } from 'core/types';
import { IUpsertPartBomForm, UpsertPartBomCreateType } from 'partBoms/components/UpsertPartBom';
import { AdvancedPartBomFormField } from 'partBoms/elements';
import { IPartBom } from 'partBoms/types';

export const CreateTypeFormField = () => {
  const { t } = useTranslation();

  const { control, errors, setValue, watch, dirtyFields } = useFormContext();

  const createTypeValue = watch('createType');

  const onChangeCreateType = (event: SyntheticEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;

    if (value === UpsertPartBomCreateType.EXISTING) {
      return;
    }

    setValue('sourcePartBom', null);

    const fieldsToReset = ['bomType', 'description', 'uom', 'taxonomy'] as const;

    fieldsToReset.forEach((field) => {
      if (dirtyFields[field]) {
        setValue(field, null);
      }
    });
  };

  const onChangeSourcePartBom = (sourcePartBom: IPartBom) => {
    setValue('sourcePartBom', sourcePartBom);
    setValue('bomType', sourcePartBom?.bomType);
    setValue('description', sourcePartBom?.descriptionLong);
    setValue('uom', sourcePartBom?.uom);
    setValue('taxonomy', sourcePartBom?.taxonomyPath);
  };

  return (
    <FormField
      name="createType"
      type={InputType.RADIO_GROUP}
      inputProps={{
        control,
        error: errors.createType,
        renderRadio: (choice: IRadioChoice) => {
          const isChoiceSelected = choice.value === createTypeValue;

          return (
            <Stack key={choice.value} gap={1}>
              <Radio value={choice.value} label={choice.label} onChange={onChangeCreateType} />

              {renderRadioChoiceFields(choice, {
                control: control as unknown as Control<IUpsertPartBomForm>,
                errors,
                isChoiceSelected,
                onChangeSourcePartBom,
              })}
            </Stack>
          );
        },
        choices: [
          {
            label: t('labelNewPartBomChoice'),
            value: UpsertPartBomCreateType.NEW,
          },
          {
            label: t('labelExistingPartBomChoice'),
            value: UpsertPartBomCreateType.EXISTING,
          },
        ],
        wrapperStyle: { gap: 2 },
      }}
    />
  );
};

const renderRadioChoiceFields = (
  choice: IRadioChoice,
  {
    control,
    errors,
    isChoiceSelected,
    onChangeSourcePartBom,
  }: {
    control: Control<IUpsertPartBomForm>;
    errors: FieldErrors<IUpsertPartBomForm>;
    isChoiceSelected: boolean;
    onChangeSourcePartBom: (sourcePartBom: IPartBom) => void;
  }
) => {
  if (!isChoiceSelected) {
    return null;
  }

  return (
    <>
      {choice.value === UpsertPartBomCreateType.EXISTING && (
        <AdvancedPartBomFormField
          name="sourcePartBom"
          onChangePartBom={onChangeSourcePartBom}
          label={null}
          placeholder={t('placeholderPartBomSource')}
          level={1}
        />
      )}

      <FormField
        name="number"
        type={InputType.TEXT_INPUT}
        inputProps={{
          control,
          error: errors.number,
          placeholder:
            choice.value === UpsertPartBomCreateType.EXISTING
              ? t('placeholderPartBomNewNumber')
              : t('placeholderPartBomNumber'),
          disabled: !isChoiceSelected,
          required: isChoiceSelected,
          slotProps: {
            input: {
              sx: {
                textTransform: 'uppercase',

                '::placeholder': {
                  textTransform: 'none',
                },
              },
            },
          },
        }}
      />
    </>
  );
};
